<template>
  <div class="flip-card-raffle">
    <div class="flip-card-container" :style="containerStyle">
      <div v-for="(prize, index) in prizes" :key="index" :style="commonCardStyle" class="flip-card" :class="{ 'is-flipped': flippedCards.includes(index) }">
        <div class="flip-card-inner">
          <div class="flip-card-front" :style="getCardStyle(false)" @click="handleClip(index)">
            <div class="flip-card-front-text">
              <p :style="{ color: prize.color }">{{ prize.text }}</p>
            </div>
          </div>
          <div class="flip-card-back" :style="getCardStyle(true, prize.src)">
            <div class="flip-card-front-text">
              <template v-for="vo in prize.srcVo" :key="vo">
                <img v-if="vo.src" :src="vo.src" :style="{ width: prize.width, height: prize.height, top: prize.top }" alt="prize" />
                <p :style="{ position: 'absolute', color: prize.fontColor, fontSize: prize.fontSize, top: prize.fontPositionY, left: prize.fontPositionX }">{{ 'x' + (vo.num || 0) }}</p>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  prizes: {
    type: Array,
    required: true,
    default: () => []
  },
  defaultConfig: {
    type: Object,
    default: () => ({})
  },
  defaultStyle: {
    type: Object,
    default: () => ({})
  },
  activeStyle: {
    type: Object,
    default: () => ({})
  },
  width: {
    type: String,
    default: '100%'
  },
  height: {
    type: String,
    default: '100%'
  },
  initFlippedCards: {
    type: Array,
    default: () => []
  }
})

const emits = defineEmits(['clip'])

// 已翻牌的奖项
const flippedCards = ref(props.initFlippedCards)

watch(
  () => props.initFlippedCards,
  (val) => {
    flippedCards.value = val
  },
  {
    immediate: true
  }
)

// 判断布局
const displayValue = computed(() => (props.width !== '0px' && props.height !== '0px' ? 'grid' : 'flex'))

// 容器样式
const containerStyle = computed(() => {
  const display = displayValue.value
  const style = {
    display,
    width: display === 'grid' ? props.width : '100%',
    height: display === 'grid' ? props.height : 'auto',
    gap: `${props.defaultConfig.verticalGutter}px ${props.defaultConfig.horizontalGutter}px`
  }
  if (display === 'grid') {
    style['grid-template-columns'] = `repeat(${props.defaultConfig.cols},1fr)`
  } else {
    style['flex-direction'] = 'row'
    style['flex-wrap'] = 'wrap'
  }
  return style
})

// 通用卡片样式
const commonCardStyle = computed(() => {
  const styleItem = {
    borderRadius: props.defaultStyle.borderRadius
  }
  if (displayValue.value === 'flex') {
    styleItem.width = props.defaultStyle.cardWidth
    styleItem.height = props.defaultStyle.cardHeight
  }
  return styleItem
})

// 卡片样式
const getCardStyle = (isFlip, src) => {
  const style = isFlip ? props.activeStyle : props.defaultStyle
  const styleItem = {
    ...style,
    ...commonCardStyle.value,
    'background-size': 'cover'
  }
  if (src) {
    styleItem.backgroundImage = `url('${src}')`
  }
  return styleItem
}

// 点击翻牌
const handleClip = (index) => {
  if (!flippedCards.value.includes(index)) {
    emits('clip', index)
  }
}

// 翻牌
const play = (index) => {
  if (!flippedCards.value.includes(index)) {
    flippedCards.value.push(index)
  }
}

// 一键翻牌
const playAll = async () => {
  let beginIndex = 0
  while (props.prizes.length !== flippedCards.value.length) {
    play(beginIndex)
    await new Promise((resolve) => setTimeout(resolve, 500))
    beginIndex++
  }
}

// 重置洗牌
const reset = () => {
  flippedCards.value = []
}

defineExpose({
  play,
  playAll,
  reset
})
</script>

<style lang="scss" scoped>
.flip-card-raffle {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

.flip-card {
  background-color: transparent;
  perspective: 1000px;
  cursor: pointer;
  text-align: center;
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;
  -webkit-transform: translate3d(0, 0, 0);
}

.flip-card-inner {
  position: relative;
  width: 100%;
  height: 100%;
  text-align: center;
  transition: transform 0.6s;
  transform-style: preserve-3d;
  -webkit-transform-style: preserve-3d;
  -webkit-backface-visibility: hidden;
  background-color: transparent;
}

.flip-card.is-flipped .flip-card-inner {
  transform: rotateY(180deg);
}

.flip-card.is-active {
  box-shadow: 0 0 10px rgba(255, 215, 0, 0.8);
}

.flip-card-front,
.flip-card-back {
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
}

.flip-card-front {
  color: black;

  &-text {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    align-items: center;

    img {
      position: relative;
    }
  }
}

.flip-card-back {
  color: white;
  transform: rotateY(180deg);
}

img {
  max-width: 100%;
  max-height: 100%;
}

.flex-row {
  display: flex;
  justify-content: center;
  align-items: center;
}
.flip-card-front-text {
  position: relative;
}
</style>
